import { Link } from "react-router-dom";
import { CircleAlert, CircleCheck } from "lucide-react";
import { codeInfo } from "@/lib/api/codes";
import type { Finding } from "@/lib/api/errors";
import { humanize } from "@/lib/status";

export type Fix = { label: string; to: string };

/** Gate, publish, payout and send findings as a checklist: a plain title, the server's detail,
 * and a fix link where the screen knows one. Blocking findings say so in words (a disabled button
 * with no reason is a dead end). The raw code stays in a tooltip for support. */
export function FindingsList({
  findings,
  fixes = {},
  title,
}: {
  findings: (Finding & { overridable?: string | null })[];
  fixes?: Record<string, Fix>;
  title?: string;
}) {
  if (findings.length === 0) {
    return (
      <p className="text-ok flex items-center gap-2 text-sm" role="status">
        <CircleCheck aria-hidden className="size-4" /> Everything checks out.
      </p>
    );
  }
  return (
    <section aria-label={title ?? "Things to fix"}>
      {title && <h3 className="mb-2 text-sm">{title}</h3>}
      <ul className="space-y-2">
        {findings.map((f, i) => {
          const info = codeInfo(f.code);
          const heading =
            info.title === codeInfo("unknown_error").title ? humanize(f.code) : info.title;
          const fix = fixes[f.code];
          return (
            <li
              key={`${f.code}-${i}`}
              className="border-danger-bd bg-danger-bg text-foreground flex items-start gap-2 rounded-md border p-3"
              title={f.code}
            >
              <CircleAlert aria-hidden className="text-danger mt-0.5 size-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{heading}</p>
                {f.detail && <p className="text-muted-foreground text-sm">{f.detail}</p>}
                {f.overridable && (
                  <p className="text-muted-foreground text-xs">
                    A Reviewer/Admin can override this with a reason.
                  </p>
                )}
              </div>
              {fix && (
                <Link to={fix.to} className="shrink-0 text-sm font-medium">
                  {fix.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
